'use client';

import { useEffect, useState } from 'react';
import { FeatureGrid } from '../content-types/FeatureGrid';
import { DOTCMS_URL } from '@/app/utils/gql';
import { DEFAULT_LANGUAGE_ID } from '@/app/utils/constants';
import Contentlet from './contentlet';

/**
 * Component to render FeatureGrid by identifier
 * Supports both server-side rendering (via contentlet prop) and client-side fetching
 * @param {string} identifier - The FeatureGrid identifier
 * @param {Object} contentlet - Optional pre-fetched contentlet data (for SSR)
 */
export default function FeatureGridByIdentifier({ identifier, contentlet: serverContentlet }) {
    const [contentlet, setContentlet] = useState(serverContentlet || null);
    const [loading, setLoading] = useState(!serverContentlet);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (serverContentlet) {
            return;
        }

        const fetchContent = async () => {
            if (!identifier) {
                setLoading(false);
                return;
            }

            try {
                const apiUrl = `${DOTCMS_URL.origin}/api/v1/content/query?query=+identifier:${identifier}&contentType=FeatureGrid&depth=3&languageId=${DEFAULT_LANGUAGE_ID}`;

                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.NEXT_PUBLIC_DOTCMS_AUTH_TOKEN}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                const fetchedContentlet = result?.contentlets?.[0];

                if (fetchedContentlet) {
                    setContentlet(fetchedContentlet);
                }
            } catch (err) {
                console.error('Error fetching FeatureGrid by identifier:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [identifier, serverContentlet]);

    if (loading) {
        return null;
    }

    if (error || !contentlet) {
        return null;
    }

    return (
        <Contentlet contentlet={contentlet}>
            <FeatureGrid {...contentlet} />
        </Contentlet>
    );
}
